import React, {useState} from 'react';
import {UploadFileSelectionDialog} from './UploadFileSelectionDialog';
import {BreadcrumbsDTO} from '../../../models/dto/BreadcrumbsDTO';
import {useGetStorageObject} from '../../../hooks/UseGetStorageObject';
import {StorageObject} from '../../../models/StorageObject';
import {DisclaimerDTO} from '../../../models/dto/DisclaimerDTO';
import {LabelDTO} from '../../../models/dto/LabelDTO';
import {StorageObjectDTO} from '../../../models/dto/StorageObjectDTO';
import {getPath} from '../../../utils';
import {generateIdentifier} from '../../../utils/IdentityUtils';
import {FileMetaDataDialog} from './FileMetaDataDialog';

export interface UploadFileDialogProps {
    readonly item: StorageObject | null;
    readonly breadcrumbs: BreadcrumbsDTO[];
    readonly currentPath: string;
    readonly rolesDataSource: string[];
    readonly parentOwnerRoles: string[];
    readonly parentRoles: string[];
    readonly parentLocales: string[];
    readonly language: string;
    readonly defaultLocale: string;
    readonly disclaimerData: DisclaimerDTO[];
    readonly labelData: LabelDTO[];
    onClose: () => void;
    onSave: (storageObjectDTO: StorageObjectDTO, thumbnail: File | null, executeFileUpload: boolean, file: File, addMore: boolean) => void;
}

const checkExecuteFileUpload = (storageObject: StorageObject, file: File): boolean => {
    return ((storageObject?.name !== file?.name) || (storageObject?.totalSize !== file?.size)) as boolean;
}

/**
 * @name UploadFileDialog
 * @description
 * A dialog that wraps the complete flow to upload a new file
 */
export const UploadFileDialog: React.VFC<UploadFileDialogProps> = (props) => {
    const getStorageObject = useGetStorageObject();
    const [file, setFile] = useState<File | null>(null);
    const [editedItem, setEditedItem] = useState<StorageObject | null>(props.item);
    const [executeFileUpload, setExecuteFileUpload] = useState<boolean>(false);

    const handleOnSave = (storageObjectDTO: StorageObjectDTO, thumbnail: File | null, addMore: boolean) => {
        if (file) {
            props.onSave(storageObjectDTO, thumbnail, executeFileUpload, file, addMore);
        }
    };

    const onContinueHandler = async (file: File) => {
        if (!editedItem) {
            let path = getPath(props.currentPath, file.name);
            let storageObject = await getStorageObject(encodeURI(path));
            if (storageObject) {
                storageObject.totalSize = file.size;
            }

            setEditedItem(storageObject);
            setExecuteFileUpload(checkExecuteFileUpload(storageObject, file));
        }

        setFile(file);
    };

    const renderDialogs = () => {
        if (editedItem) {
            return (
                <FileMetaDataDialog item={editedItem}
                                    file={file}
                                    currentPath={props.currentPath}
                                    parentOwnerRoles={props.parentOwnerRoles}
                                    parentRoles={props.parentRoles}
                                    parentLocales={props.parentLocales}
                                    language={props.language}
                                    defaultLocale={props.defaultLocale}
                                    disclaimerData={props.disclaimerData}
                                    labelData={props.labelData}
                                    rolesDataSource={props.rolesDataSource}
                                    onClose={props.onClose}
                                    onSave={handleOnSave}/>
            );
        } else if (!file) {
            return (
                <UploadFileSelectionDialog breadcrumbs={props.breadcrumbs}
                                           currentPath={props.currentPath}
                                           onClose={props.onClose}
                                           onContinue={onContinueHandler}/>
            );
        } else {
            return (
                <FileMetaDataDialog item={editedItem}
                                    file={file}
                                    currentPath={props.currentPath}
                                    parentOwnerRoles={props.parentOwnerRoles}
                                    parentRoles={props.parentRoles}
                                    parentLocales={props.parentLocales}
                                    language={props.language}
                                    defaultLocale={props.defaultLocale}
                                    disclaimerData={props.disclaimerData}
                                    labelData={props.labelData}
                                    rolesDataSource={props.rolesDataSource}
                                    onClose={props.onClose}
                                    onSave={handleOnSave}/>
            );
        }
    };

    return <div id={generateIdentifier()}>{renderDialogs()}</div>;
};
